import { z } from 'zod';
import { UserRole, ContentType, Platform, CampaignStatus } from './enums.js';
import { PAGINATION_DEFAULTS } from './constants.js';

// --- Auth Schemas ---

export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const registerSchema = z.object({
  email: z.string().trim().toLowerCase().email('Invalid email address'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(128, 'Password must not exceed 128 characters'),
  name: z.string().min(1, 'Name is required').max(255),
  role: z.nativeEnum(UserRole),
});
export type RegisterInput = z.infer<typeof registerSchema>;

// --- Campaign Schemas ---

export const createCampaignSchema = z.object({
  name: z.string().min(1, 'Campaign name is required').max(255),
  description: z.string().max(2000).optional(),
  brief: z.string().min(1, 'Campaign brief is required'),
  deliverableCount: z.number().int().positive(),
  deliverableTypes: z.array(z.nativeEnum(ContentType)).min(1),
  budget: z.string().optional(),
  paymentPerDeliverable: z.string().optional(),
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  status: z.nativeEnum(CampaignStatus).default(CampaignStatus.DRAFT),
  clientId: z.string().uuid(),
  guidelines: z.record(z.unknown()).optional(),
});
export type CreateCampaignInput = z.infer<typeof createCampaignSchema>;

// --- Submission Schemas ---

export const createSubmissionSchema = z.object({
  campaignId: z.string().uuid(),
  contentType: z.nativeEnum(ContentType),
  platform: z.nativeEnum(Platform),
  caption: z.string().min(1, 'Caption is required').max(2200),
  hashtags: z.array(z.string()).default([]),
  mediaUrl: z.string().url(),
  thumbnailUrl: z.string().url().optional(),
  duration: z.number().int().positive().optional(),
});
export type CreateSubmissionInput = z.infer<typeof createSubmissionSchema>;

// ─── Structured Brief (Phase 4, goal-alignment B3) ───────────────────────────
//
// Stored inside Campaign.guidelines (Json?) — NO new column.
// Discriminator: presence of `version: 'v1'` at the top level.
// A legacy/absent guidelines value (null or any object without `version`)
// is a free-text-only brief and must always be accepted (backward compat, R4).
//
// Both the admin authoring form and the influencer checklist import from here
// — one source of truth (Rule 11 contract-first).

/** One required checklist item in the structured brief. */
export const briefChecklistItemSchema = z.object({
  /** Stable, client-generated id (e.g. UUID or nanoid) for keyed rendering. */
  id: z.string().min(1).max(100),
  /** Human-readable instruction text shown to the creator. */
  text: z.string().min(1).max(500),
});
export type BriefChecklistItem = z.infer<typeof briefChecklistItemSchema>;

/** One good/bad example pair (both fields are optional to allow partial examples). */
export const briefExampleSchema = z.object({
  good: z.string().max(1000).optional(),
  bad: z.string().max(1000).optional(),
});
export type BriefExample = z.infer<typeof briefExampleSchema>;

/**
 * Structured brief shape stored in Campaign.guidelines.
 * Version discriminator: `version === 'v1'`.
 *
 * All fields beyond `version` are optional — a freshly-created structured
 * brief may start with just the version tag and be filled in incrementally.
 */
export const structuredBriefSchema = z.object({
  /** Discriminator — always `'v1'`. Increment when shape is breaking. */
  version: z.literal('v1'),
  /** Required checklist items the creator must complete for each deliverable. */
  checklistItems: z.array(briefChecklistItemSchema).max(30).optional(),
  /** Positive instructions (what the creator SHOULD do). */
  dos: z.array(z.string().min(1).max(500)).max(20).optional(),
  /** Negative instructions (what the creator must AVOID). */
  donts: z.array(z.string().min(1).max(500)).max(20).optional(),
  /** Optional good/bad examples to illustrate expectations. */
  examples: z.array(briefExampleSchema).max(10).optional(),
});
export type StructuredBrief = z.infer<typeof structuredBriefSchema>;

/**
 * Returns true when the value stored in Campaign.guidelines is a
 * structured brief (v1 discriminator present). Consumers use this to
 * decide whether to render a checklist or fall back to the free-text `brief`.
 */
export function isStructuredBrief(guidelines: unknown): guidelines is StructuredBrief {
  return (
    typeof guidelines === 'object' &&
    guidelines !== null &&
    (guidelines as Record<string, unknown>)['version'] === 'v1'
  );
}

/**
 * Derives an ordered list of requirement strings from a campaign brief + guidelines.
 *
 * 1. If `guidelines` is a structured v1 brief, extracts each checklistItem.text (trimmed,
 *    empties dropped) in array order.
 * 2. Otherwise falls back to `brief` free-text: splits on '\n', trims, drops empty lines,
 *    strips a leading '- ' or '* ' bullet prefix.
 * 3. Appends each non-blank requiredHashtags entry as 'Required hashtag: #<tag>'.
 *
 * Pure function, no side effects.
 */
export function deriveBriefRequirements(input: {
  brief?: string | null;
  guidelines?: unknown;
  requiredHashtags?: string[];
}): string[] {
  const { brief, guidelines, requiredHashtags } = input;
  const requirements: string[] = [];

  if (isStructuredBrief(guidelines)) {
    // Structured v1 path: use checklist items
    for (const item of guidelines.checklistItems ?? []) {
      const text = item.text.trim();
      if (text.length > 0) {
        requirements.push(text);
      }
    }
  } else {
    // Free-text fallback: split brief on newlines, strip bullets
    if (brief) {
      for (const raw of brief.split('\n')) {
        const trimmed = raw.trim();
        if (trimmed.length === 0) continue;
        // Strip a single leading bullet marker ('- ' or '* ')
        const stripped = trimmed.replace(/^[-*]\s+/, '');
        requirements.push(stripped);
      }
    }
  }

  // Always append required hashtags
  for (const tag of requiredHashtags ?? []) {
    const t = tag.trim();
    if (t.length > 0) {
      requirements.push(`Required hashtag: #${t}`);
    }
  }

  return requirements;
}

// --- Pagination Schema ---

export const paginationSchema = z.object({
  page: z.coerce.number().int().positive().default(PAGINATION_DEFAULTS.page),
  limit: z.coerce
    .number()
    .int()
    .positive()
    .max(PAGINATION_DEFAULTS.maxLimit)
    .default(PAGINATION_DEFAULTS.limit),
  sort: z.string().optional(),
  order: z.enum(['asc', 'desc']).default('desc'),
});
export type PaginationInput = z.infer<typeof paginationSchema>;
